import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { partialSplit } from "@/lib/partialPaid";

const money = (value) => (Number(value) || 0).toLocaleString("en-AU", { style: "currency", currency: "AUD" });
const shown = (value) => (value === null || value === undefined ? "" : String(value));

// The Partial Paid box: amount claimed from VLA, and the remainder to pay from the budget
// (worked out from the invoice total until it is typed in). Saved when a field is left, via
// `onCommit({ vlaClaimed, fromBudget })`; fromBudget is "" while it is still the worked-out figure.
export default function PartialPaidSplit({ idPrefix, total, vlaClaimed, fromBudget, onCommit, disabled = false, budgetLabel = "budget" }) {
  const [claimed, setClaimed] = useState(shown(vlaClaimed));
  const [budget, setBudget] = useState(shown(fromBudget));
  useEffect(() => { setClaimed(shown(vlaClaimed)); }, [vlaClaimed]);
  useEffect(() => { setBudget(shown(fromBudget)); }, [fromBudget]);

  const split = partialSplit(total, claimed, budget);
  const commit = () => {
    if (claimed === shown(vlaClaimed) && budget === shown(fromBudget)) return;
    onCommit({ vlaClaimed: claimed, fromBudget: budget });
  };

  return (
    <div className="space-y-2 rounded-md border border-violet-200 bg-violet-50/60 p-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wide text-violet-800">Partial paid</span>
        {Number(total) > 0 && <span className="text-[10px] text-slate-600">Invoice {money(total)}</span>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label htmlFor={`${idPrefix}-vla`} className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-500">Claimed from VLA</Label>
          <Input
            id={`${idPrefix}-vla`}
            inputMode="decimal"
            value={claimed}
            onChange={(event) => setClaimed(event.target.value)}
            onBlur={commit}
            disabled={disabled}
            placeholder="0.00"
            className="h-8 bg-white text-xs"
          />
        </div>
        <div>
          <Label htmlFor={`${idPrefix}-budget`} className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-500">Remaining to pay from {budgetLabel}</Label>
          <Input
            id={`${idPrefix}-budget`}
            inputMode="decimal"
            value={budget}
            onChange={(event) => setBudget(event.target.value)}
            onBlur={commit}
            disabled={disabled}
            placeholder={split.fromBudget !== null && !split.budgetTyped ? String(split.fromBudget.toFixed(2)) : "0.00"}
            className="h-8 bg-white text-xs"
          />
        </div>
      </div>
      {split.fromBudget !== null && !split.budgetTyped && (
        <p className="text-[10px] text-slate-500">Remaining worked out as the invoice less the VLA claim: {money(split.fromBudget)}.</p>
      )}
      {split.mismatch && (
        <p className="text-[10px] text-amber-700">These add up to {money(split.vlaClaimed + split.fromBudget)}, not the invoice&apos;s {money(total)}.</p>
      )}
    </div>
  );
}
